// src/curated/hotelSearch.js
// Live hotels for a curated trip stay, from the same /hotels API the trip planner and
// HotelBooking.jsx use. Results are normalised into the picker's shape and are bookable
// through /trip-planner/hotel/:hotelCode (see handoffLiveHotel).

import { HOTEL_API } from "../creator/creatorTripEngine";

// Same base the engine uses (/development in dev builds), so both calls hit one backend
export const HOTELS_API = `${HOTEL_API}/hotels`;
const HANDOFF_PREFIX = "rc_hotel_handoff_"; // must match HotelBookingPage.jsx
const TIMEOUT_MS = 25000;
const MAX_RESULTS = 12;

// Per-night price bands used to place live hotels into the curator's tiers
export const tierForPrice = (pn) => (pn < 3500 ? "budget" : pn < 9000 ? "comfort" : "premium");

const toApiDate = (iso) => { const [y, m, d] = iso.split("-"); return `${d}-${m}-${y}`; };
const isoAdd = (iso, n) => {
    const [y, m, d] = iso.split("-").map(Number);
    const x = new Date(y, m - 1, d + n);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const num = (v) => { const n = typeof v === "string" ? parseFloat(v) : v; return Number.isFinite(n) ? n : 0; };

export const getUserId = () => {
    try { return localStorage.getItem("routechef_user_id") || ""; } catch { return ""; }
};

/** Check-in / check-out (yyyy-mm-dd) for a stay that starts on day index `dayIndex`. */
export const stayDates = (startDate, dayIndex, nights) => ({
    checkIn: isoAdd(startDate, dayIndex),
    checkOut: isoAdd(startDate, dayIndex + nights),
});

/** Cheapest room of a raw /hotels result, total for the whole stay incl. hotel taxes */
const cheapestRoom = (rooms = []) => {
    let best = null;
    for (const r of rooms) {
        const total = num(r.TotalFare ?? r.total_fare) + num(r.TotalTax ?? r.total_tax);
        if (total > 0 && (!best || total < best.total)) best = { room: r, total };
    }
    return best;
};

const MEAL = { Room_Only: "Room only", BreakFast: "Breakfast", Breakfast_For_2: "Breakfast", Half_Board: "Half board", Full_Board: "All meals", All_Inclusive: "All inclusive" };

export const normaliseHotel = (h, { nights, checkIn, checkOut, rooms }) => {
    const best = cheapestRoom(h.rooms || h.Rooms || []);
    if (!h.hotel_code || !best) return null;
    const pn = Math.round(best.total / Math.max(1, nights));
    const meal = best.room.MealType ?? best.room.meal_type;
    const images = [...new Set([h.image, h.Image, h.hotel_image, ...(h.images || h.Images || [])].filter(Boolean))];
    return {
        code: `live:${h.hotel_code}`,
        hotel_code: h.hotel_code,
        live: true,
        name: h.hotel_name || h.HotelName || h.name || "Hotel",
        tier: tierForPrice(pn),
        price_pn: pn,
        total: Math.round(best.total),
        rating: num(h.hotel_rating ?? h.star_rating ?? h.StarRating ?? h.rating) || null,
        area: h.hotel_area || h.address || h.Address || h.city_name || "",
        meal: MEAL[meal] || (meal ? String(meal).replace(/_/g, " ") : ""),
        refundable: !!(best.room.IsRefundable ?? best.room.is_refundable),
        image: images[0] || "",
        perks: [best.room.IsRefundable || best.room.is_refundable ? "Free cancellation" : null, "Instant booking"].filter(Boolean),
        // kept for the booking handoff
        _raw: { ...h, rooms: h.rooms || h.Rooms || [] },
        _room: best.room,
        check_in: checkIn,
        check_out: checkOut,
        no_of_rooms: rooms,
    };
};

/**
 * Search live hotels for one stay. Returns normalised results, cheapest first.
 * Never throws: returns [] on any failure so the picker falls back to the shortlist.
 */
export async function searchStayHotels({ place, startDate, dayIndex, nights, rooms, signal }) {
    const { checkIn, checkOut } = stayDates(startDate, dayIndex, nights);
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
    if (signal && ctrl) signal.addEventListener("abort", () => ctrl.abort(), { once: true });
    try {
        const res = await fetch(HOTELS_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: ctrl?.signal,
            body: JSON.stringify({
                place_name: place,
                check_in: toApiDate(checkIn),
                check_out: toApiDate(checkOut),
                radius_km: 30,
                user_id: getUserId(),
                no_of_rooms: rooms,
            }),
        });
        if (!res.ok) return [];
        let data = await res.json();
        if (typeof data?.body === "string") data = JSON.parse(data.body);
        const list = (data?.hotels || data?.data?.hotels || [])
            .map((h) => normaliseHotel(h, { nights, checkIn, checkOut, rooms }))
            .filter(Boolean)
            .sort((a, b) => (b.rating || 0) - (a.rating || 0) || a.total - b.total);
        return list.slice(0, MAX_RESULTS);
    } catch {
        return [];
    } finally {
        if (timer) clearTimeout(timer);
    }
}

/** Open the existing hotel booking page for a live pick (same handoff the trip planner uses). */
export function handoffLiveHotel(navigate, h) {
    const raw = h._raw || {};
    try {
        sessionStorage.setItem(HANDOFF_PREFIX + h.hotel_code, JSON.stringify({
            ...raw,
            hotel_code: h.hotel_code,
            hotel_name: h.name,
            hotel_rating: h.rating,
            rooms: raw.rooms || [],
            selected_room: h._room,
            check_in: h.check_in,
            check_out: h.check_out,
            no_of_rooms: h.no_of_rooms,
            images: h.image ? [h.image] : [],
        }));
    } catch { /* storage blocked: the page refetches rooms itself */ }
    const qs = new URLSearchParams({ check_in: h.check_in, check_out: h.check_out });
    const uid = getUserId();
    if (uid) qs.set("user_id", uid);
    navigate(`/trip-planner/hotel/${encodeURIComponent(h.hotel_code)}?${qs.toString()}`);
}
